import { useEffect, useRef, useCallback } from 'react';
import {useAuthProvider} from "../providers/AuthProvider";
import utils from "../utils/commonFunctions";
import {useNavigate} from "react-router-dom";

export const useAutoRefreshToken = () => {
    const {
        tokenExpiresIn,
        storeProfileData,
        logout,
    } = useAuthProvider();
    const navigate = useNavigate();

    const refreshTimeoutRef = useRef(null);
    const isRefreshingRef = useRef(false);

    /**
     * Refresh the access token using secure HTTP-only cookies.
     * Also update the TTLs (atl, rtl) returned from the backend.
     */
    const refreshAccessToken = useCallback(async () => {
        if (isRefreshingRef.current) {
            console.log('[refresh] Skipping — refresh already in progress.');
            return;
        }

        isRefreshingRef.current = true;
        console.log('[refresh] Attempting to refresh token...');

        try {
            const refresh = await utils.refreshToken();

            const {success, data} = refresh

            if (success) {
                storeProfileData(data)
                const {atl} = data

                scheduleNextRefresh(atl);

                // console.log('[refresh] Refresh successful:', { atl, rtl });
            }else{
                console.error('[refresh] Failed to refresh token');
                await logout(); // clear user session if refresh fails
                navigate('/login', { replace: true });
            }


        } catch (error) {
            console.error('[refresh] Failed to refresh token:', error);
            await logout(); // clear user session if refresh fails
            navigate('/login', { replace: true });
        } finally {
            isRefreshingRef.current = false;
        }
    }, [logout]);

    /**
     * Schedule the next refresh attempt based on token expiry.
     * Refresh happens 60 seconds before actual expiry.
     */
    const scheduleNextRefresh = useCallback((expiresInSeconds) => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        let refreshDelaySeconds;

        if (expiresInSeconds >= 300) {
            // TTL is 5 minutes or more → refresh 1 minute early
            refreshDelaySeconds = expiresInSeconds - 60;
        } else {
            // TTL is less than 5 minutes → refresh 10 seconds early
            refreshDelaySeconds = Math.max(expiresInSeconds - 10, 1); // at least 1s
        }

        const timeoutMs = refreshDelaySeconds * 1000;
        const fireAt = new Date(Date.now() + timeoutMs).toLocaleTimeString();

        console.log(`[refresh] TTL = ${expiresInSeconds}s. Refresh scheduled in ${refreshDelaySeconds}s (at ${fireAt})`);

        refreshTimeoutRef.current = setTimeout(() => {
            console.log('[refresh] ⏰ Time reached — refreshing token...');
            refreshAccessToken();
        }, timeoutMs);
    }, [refreshAccessToken]);
    /**
     * Starts the auto-refresh flow.
     * If TTL is short or missing, refresh immediately.
     */
    const startAutoRefresh = useCallback(() => {
        if (!tokenExpiresIn) {
            console.warn('[refresh] No token TTL found. Refreshing now...');
            refreshAccessToken();
            return;
        }

        if (tokenExpiresIn > 120) {
            // If TTL > 2 mins, schedule future refresh
            scheduleNextRefresh(tokenExpiresIn);
        } else {
            // Otherwise refresh immediately
            console.log('[refresh] TTL is short. Refreshing immediately...', {tokenExpiresIn});
            refreshAccessToken();
        }
    }, [tokenExpiresIn, scheduleNextRefresh, refreshAccessToken]);

    /**
     * Hook auto-start: Runs once on mount.
     * Also clears any timeouts on unmount.
     */
    useEffect(() => {
        if (!tokenExpiresIn) {
            console.log('[refresh] No TTL at mount. Refreshing immediately...');
            refreshAccessToken();
            return;
        }

        if (tokenExpiresIn > 120) {
            scheduleNextRefresh(tokenExpiresIn);
        } else {
            console.log('[refresh] TTL too short at mount. Refreshing immediately...');
            refreshAccessToken();
        }

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                console.log('[refresh] 🔄 Cleared refresh timeout on unmount.');
            }
        };
    }, [tokenExpiresIn, refreshAccessToken, scheduleNextRefresh]);


    return { startAutoRefresh };
};